import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { GetS3List, getFolderNameFromPrefix } from '../../../services/query/aiPrompt/S3BrowserQuery.services';
import {
    DOWNLOAD_CONCURRENCY,
    DownloadItemStatus,
    DownloadQueueItem,
    attachPresignedUrlsToDownloadItems,
    createInitialDownloadItems,
    mergeRefreshedDownloadItems,
    processDownloadItemAttempts,
    resolveDownloadFolderName,
    resolveDownloadTargetDirectory,
    runParallelDownloadWorkers,
} from './SCUIS3Browser.helpers';

type UseSCUIS3BrowserDownloadsParams = {
    prefix: string;
    effectiveBasePrefix: string;
    onDownloadingChange?: (downloading: boolean) => void;
};

export function useSCUIS3BrowserDownloads({
    prefix,
    effectiveBasePrefix,
    onDownloadingChange,
}: UseSCUIS3BrowserDownloadsParams) {
    const { t } = useTranslation();
    const [downloading, setDownloading] = useState(false);
    const [downloadItems, setDownloadItems] = useState<DownloadQueueItem[]>([]);
    const [downloadStatusExpanded, setDownloadStatusExpanded] = useState(true);
    const [downloadElapsedMs, setDownloadElapsedMs] = useState(0);
    const [lastDownloadDurationMs, setLastDownloadDurationMs] = useState<number | null>(null);
    const [presignedCorsAvailable, setPresignedCorsAvailable] = useState<boolean | null>(null);

    const downloadTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const downloadStartedAtRef = useRef<number | null>(null);
    const downloadTargetDirRef = useRef<FileSystemDirectoryHandle | null>(null);
    const onDownloadingChangeRef = useRef(onDownloadingChange);
    onDownloadingChangeRef.current = onDownloadingChange;

    const setDownloadingState = useCallback((value: boolean) => {
        setDownloading(value);
        onDownloadingChangeRef.current?.(value);
    }, []);

    const updateDownloadItem = useCallback((id: string, patch: Partial<DownloadQueueItem>) => {
        setDownloadItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...patch } : item)));
    }, []);

    const clearDownloadTimer = useCallback(() => {
        if (downloadTimerRef.current != null) {
            clearInterval(downloadTimerRef.current);
            downloadTimerRef.current = null;
        }
    }, []);

    const startDownloadTimer = useCallback(() => {
        clearDownloadTimer();
        downloadStartedAtRef.current = performance.now();
        setDownloadElapsedMs(0);
        setLastDownloadDurationMs(null);
        downloadTimerRef.current = setInterval(() => {
            if (downloadStartedAtRef.current != null) {
                setDownloadElapsedMs(Math.round(performance.now() - downloadStartedAtRef.current));
            }
        }, 250);
    }, [clearDownloadTimer]);

    const stopDownloadTimer = useCallback(() => {
        clearDownloadTimer();
        if (downloadStartedAtRef.current != null) {
            const duration = Math.round(performance.now() - downloadStartedAtRef.current);
            setLastDownloadDurationMs(duration);
            setDownloadElapsedMs(duration);
            downloadStartedAtRef.current = null;
        }
    }, [clearDownloadTimer]);

    useEffect(() => () => clearDownloadTimer(), [clearDownloadTimer]);

    useEffect(() => {
        if (!downloading) return;
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            event.returnValue = ''; // TODO: remove this line
        };
        window.addEventListener('beforeunload', onBeforeUnload);
        return () => window.removeEventListener('beforeunload', onBeforeUnload);
    }, [downloading]);

    const runDownloadQueue = useCallback(
        async (items: DownloadQueueItem[], preferPresigned: boolean) => {
            setDownloadingState(true);
            setDownloadStatusExpanded(true);

            try {
                const processItem = async (item: DownloadQueueItem) => {
                    await processDownloadItemAttempts(
                        item,
                        {
                            preferPresigned,
                            presignedCorsAvailable,
                            updateDownloadItem,
                            setPresignedCorsAvailable,
                        },
                        { downloadTargetDir: downloadTargetDirRef.current },
                        updateDownloadItem
                    );
                };

                await runParallelDownloadWorkers(items, DOWNLOAD_CONCURRENCY, processItem);
            } finally {
                stopDownloadTimer();
                setDownloadingState(false);
            }
        },
        [presignedCorsAvailable, setDownloadingState, stopDownloadTimer, updateDownloadItem]
    );

    const startParallelDownloads = useCallback(
        async (keysWithSizes: { key: string; size: number }[], folderName?: string) => {
            if (!keysWithSizes.length) return;

            const initialItems = createInitialDownloadItems(keysWithSizes);
            setDownloadItems(initialItems);
            setDownloadStatusExpanded(true);
            downloadTargetDirRef.current = null;

            const resolvedFolderName = resolveDownloadFolderName(
                folderName,
                keysWithSizes.length,
                prefix,
                effectiveBasePrefix
            );

            const pickerResult = await resolveDownloadTargetDirectory(keysWithSizes.length, resolvedFolderName, {
                pickFolderTitle: t('aiPrompt.s3Browser.downloadPickFolderTitle'),
                pickFolderText: t('aiPrompt.s3Browser.downloadPickFolderText', { folder: resolvedFolderName ?? '' }),
                ok: t('general.ok'),
                folderCancelled: t('aiPrompt.s3Browser.downloadFolderCancelled'),
                folderFallback: t('aiPrompt.s3Browser.downloadFolderFallback'),
                folderUnsupported: t('aiPrompt.s3Browser.downloadFolderUnsupported'),
            });

            if (pickerResult.status === 'cancelled') {
                setDownloadItems([]);
                return;
            }

            if (pickerResult.status === 'ready') {
                downloadTargetDirRef.current = pickerResult.directory;
            }

            setDownloadingState(true);
            startDownloadTimer();

            try {
                const withUrls = await attachPresignedUrlsToDownloadItems(initialItems);
                setDownloadItems(withUrls);
                await runDownloadQueue(withUrls, true);
            } catch {
                await runDownloadQueue(initialItems, false);
            }
        },
        [effectiveBasePrefix, prefix, runDownloadQueue, setDownloadingState, startDownloadTimer, t]
    );

    const retryFailedDownloads = useCallback(async () => {
        const failed = downloadItems.filter((item) => item.status === DownloadItemStatus.Failed);
        if (!failed.length || downloading) return;

        const reset = failed.map((item) => ({
            ...item,
            progress: 0,
            status: DownloadItemStatus.Pending,
            attempt: 0,
            error: undefined,
        }));

        setDownloadItems((prev) => mergeRefreshedDownloadItems(prev, reset));

        startDownloadTimer();
        setDownloadingState(true);
        await runDownloadQueue(reset, true);
    }, [downloadItems, downloading, runDownloadQueue, setDownloadingState, startDownloadTimer]);

    const downloadFile = useCallback(
        async (file: { key: string; size: number }, closeCtxMenu: () => void) => {
            closeCtxMenu();
            if (downloading) return;
            await startParallelDownloads([{ key: file.key, size: file.size }]);
        },
        [downloading, startParallelDownloads]
    );

    const downloadFolder = useCallback(
        async (folderKey: string, closeCtxMenu: () => void) => {
            closeCtxMenu();
            if (downloading) return;
            try {
                setDownloadStatusExpanded(true);
                const response = await GetS3List({ prefix: folderKey });
                if (!response.files || response.files.length === 0) {
                    await Swal.fire({ icon: 'info', title: t('aiPrompt.s3Browser.empty') });
                    return;
                }

                const keysWithSizes = response.files.map((file) => ({
                    key: file.key,
                    size: file.size,
                }));
                await startParallelDownloads(keysWithSizes, getFolderNameFromPrefix(folderKey));
            } catch {
                await Swal.fire({ icon: 'error', title: t('aiPrompt.s3Browser.downloadFolderError') });
            }
        },
        [downloading, startParallelDownloads, t]
    );

    const downloadSelected = useCallback(
        async (selectedKeys: Set<string>, fileList: { key: string; size: number }[]) => {
            if (downloading) return;
            if (selectedKeys.size === 0) {
                await Swal.fire({ icon: 'info', title: t('aiPrompt.s3Browser.nothingSelected') });
                return;
            }

            const keysWithSizes = Array.from(selectedKeys).map((key) => {
                const file = fileList.find((entry) => entry.key === key);
                return { key, size: file?.size ?? 0 };
            });
            await startParallelDownloads(keysWithSizes, getFolderNameFromPrefix(prefix || effectiveBasePrefix));
        },
        [downloading, effectiveBasePrefix, prefix, startParallelDownloads, t]
    );

    const clearDownloadPanel = useCallback(() => {
        setDownloadItems([]);
        setLastDownloadDurationMs(null);
        setDownloadElapsedMs(0);
    }, []);

    const downloadCompletedCount = downloadItems.filter((item) => item.status === DownloadItemStatus.Completed).length;
    const downloadFailedCount = downloadItems.filter((item) => item.status === DownloadItemStatus.Failed).length;
    const showDownloadPanel = downloadItems.length > 0;

    return {
        downloading,
        downloadItems,
        downloadStatusExpanded,
        setDownloadStatusExpanded,
        downloadElapsedMs,
        lastDownloadDurationMs,
        downloadCompletedCount,
        downloadFailedCount,
        showDownloadPanel,
        retryFailedDownloads,
        downloadFile,
        downloadFolder,
        downloadSelected,
        clearDownloadPanel,
    };
}
